// src/services/settingsService.js
import { api } from './api';
import { createService } from './factory';

// Create base services for different settings types
const userSettingsServiceBase = createService('UserSettings', '/settings/user', {
  customEndpoints: {
    // Reset user settings
    resetUserSettings: async () => {
      const response = await api.put('/settings/user/reset');
      return response.data;
    },
    
    // Update nested user setting (e.g. notifications.email)
    updateNestedUserSetting: async (parent, key, value) => {
      const fullKey = key ? `${parent}.${key}` : parent;
      const response = await api.put('/settings/user', { key: fullKey, value });
      return response.data;
    }
  }
});

const systemSettingsServiceBase = createService('SystemSettings', '/settings/system');

const clientSettingsServiceBase = createService('ClientSettings', '/settings/client', {
  customEndpoints: {
    // Get client settings
    getClientSettings: async (clientId) => {
      const response = await api.get(`/settings/client/${clientId}`);
      return response.data;
    },
    
    // Update client setting
    updateClientSetting: async (clientId, key, value) => {
      const response = await api.put(`/settings/client/${clientId}`, { key, value });
      return response.data;
    }
  }
});

const emailSettingsServiceBase = createService('EmailSettings', '/settings/email', {
  customEndpoints: {
    // Test email settings
    testEmailSettings: async (email) => {
      const response = await api.post('/settings/email/test', { recipient: email });
      return response.data;
    }
  }
});

const smsSettingsServiceBase = createService('SmsSettings', '/settings/sms', {
  customEndpoints: {
    // Test SMS settings
    testSmsSettings: async (phoneNumber) => {
      const response = await api.post('/settings/sms/test', { recipient: phoneNumber });
      return response.data;
    }
  }
});

const generalSettingsServiceBase = createService('GeneralSettings', '/settings', {
  customEndpoints: {
    // Export settings
    exportSettings: async () => {
      const response = await api.get('/settings/export');
      return response.data;
    },
    
    // Import settings
    importSettings: async (settingsData) => {
      const response = await api.post('/settings/import', settingsData);
      return response.data;
    },
    
    // Get system information
    getSystemInfo: async () => {
      const response = await api.get('/settings/system-info');
      return response.data;
    }
  }
});

/**
 * API functions for managing user settings
 */

// Get user settings
export const getUserSettings = async () => {
  const response = await userSettingsServiceBase.getAll();
  return response.data;
};

// Update user setting
export const updateUserSetting = async (key, value) => {
  try {
    // First try with the expected RESTful endpoint
    const response = await api.put(`/settings/user/${key}`, { value });
    return response.data;
  } catch (error) {
    // If that fails, try with the alternate endpoint format as fallback
    console.log(`Falling back to alternate endpoint format for setting ${key}`);
    const response = await api.put('/settings/user', { key, value });
    return response.data;
  }
};

// Update multiple user settings at once
export const updateUserSettings = async (settings) => {
  try {
    const response = await api.put('/settings/user', { settings });
    return response.data;
  } catch (error) {
    console.error('Failed to update user settings:', error);
    throw error;
  }
};

// Reset user settings
export const resetUserSettings = async () => {
  return userSettingsServiceBase.resetUserSettings();
};

// Update nested user setting (e.g. notifications.email)
export const updateNestedUserSetting = async (parent, key, value) => {
  return userSettingsServiceBase.updateNestedUserSetting(parent, key, value);
};

/**
 * API functions for managing system settings (admin only)
 */

// Get system settings
export const getSystemSettings = async () => {
  const response = await systemSettingsServiceBase.getAll();
  return response.data;
};

// Update system setting
export const updateSystemSetting = async (key, value) => {
  const response = await api.put('/settings/system', { key, value });
  return response.data;
};

// Create system setting
export const createSystemSetting = async (settingData) => {
  const response = await systemSettingsServiceBase.create(settingData);
  return response.data;
};

/**
 * API functions for managing client settings
 */

// Get client settings
export const getClientSettings = async (clientId) => {
  return clientSettingsServiceBase.getClientSettings(clientId);
};

// Update client setting
export const updateClientSetting = async (clientId, key, value) => {
  return clientSettingsServiceBase.updateClientSetting(clientId, key, value);
};

/**
 * API functions for email and SMS settings
 */

// Get email settings
export const getEmailSettings = async () => {
  const response = await emailSettingsServiceBase.getAll();
  return response.data;
};

// Update email settings
export const updateEmailSettings = async (settings) => {
  const response = await emailSettingsServiceBase.update(null, settings);
  return response.data;
};

// Test email settings
export const testEmailSettings = async (email) => {
  return emailSettingsServiceBase.testEmailSettings(email);
};

// Get SMS settings
export const getSmsSettings = async () => {
  const response = await smsSettingsServiceBase.getAll();
  return response.data;
};

// Update SMS settings
export const updateSmsSettings = async (settings) => {
  const response = await smsSettingsServiceBase.update(null, settings);
  return response.data;
};

// Test SMS settings
export const testSmsSettings = async (phoneNumber) => {
  return smsSettingsServiceBase.testSmsSettings(phoneNumber);
};

/**
 * API functions for exporting and importing settings
 */

// Export settings
export const exportSettings = async () => {
  return generalSettingsServiceBase.exportSettings();
};

// Import settings
export const importSettings = async (settingsData) => {
  return generalSettingsServiceBase.importSettings(settingsData);
};

// Get system information
export const getSystemInfo = async () => {
  return generalSettingsServiceBase.getSystemInfo();
};

/**
 * API functions for HR settings
 */

// Get HR settings
export const getHRSettings = async () => {
  const response = await api.get('/settings/hr');
  return response.data;  // This returns { status: 'success', data: hrSettings }
};

// Update HR settings
export const updateHRSettings = async (settings) => {
  const response = await api.put('/settings/hr', settings);
  return response.data;  // This returns { status: 'success', data: hrSettings }
};